import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ListMusic, Loader2, Plus, RefreshCw, Sheet, Trash2 } from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoader } from '@/components/ui/loader';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);

// ══ Google Sheets → leads ════════════════════════════════════════════════════

type SheetSync = { id: string; name: string; csvUrl: string; mapping: Record<string, string>; isActive: boolean; lastRunAt: string | null; lastImported: number; lastError: string | null };
const FIELDS = [
  ['', 'Skip'],
  ['name', 'Full name'],
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['companyName', 'Company'],
  ['designation', 'Job title'],
  ['city', 'City'],
  ['country', 'Country'],
  ['requirement', 'Requirement'],
  ['campaign', 'Campaign'],
  ['budget', 'Budget'],
] as const;

function guess(header: string): string {
  const h = header.toLowerCase();
  if (/e-?mail/.test(h)) return 'email';
  if (/phone|mobile|whats/.test(h)) return 'phone';
  if (/first/.test(h)) return 'firstName';
  if (/last|surname/.test(h)) return 'lastName';
  if (/name/.test(h)) return 'name';
  if (/company|business/.test(h)) return 'companyName';
  if (/title|position|designation/.test(h)) return 'designation';
  if (/city|emirate/.test(h)) return 'city';
  if (/country/.test(h)) return 'country';
  if (/budget/.test(h)) return 'budget';
  if (/campaign|utm/.test(h)) return 'campaign';
  if (/message|note|requirement|interest/.test(h)) return 'requirement';
  return '';
}

/** CRM → Google Sheets: new rows in a published sheet become Incoming leads. */
export function SheetSyncPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: syncs = [], isLoading } = useQuery({ queryKey: ['sheet-syncs'], queryFn: () => apiGet<SheetSync[]>('/sheet-syncs') });
  const [adding, setAdding] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sheet-syncs'] });
  const run = useMutation({
    mutationFn: (id: string) => apiPost<{ imported: number }>(`/sheet-syncs/${id}/run`, {}),
    onSuccess: (result) => {
      toast.success(t('{count} new leads', { count: result.imported }));
      refresh();
    },
    onError: (error) => {
      failed(error, 'The sheet could not be read');
      refresh();
    },
  });
  const toggle = useMutation({ mutationFn: (sync: SheetSync) => apiPatch(`/sheet-syncs/${sync.id}`, { isActive: !sync.isActive }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => apiDelete(`/sheet-syncs/${id}`), onSuccess: refresh });
  return (
    <div>
      <PageHeader
        title="Google Sheets"
        description="Leads from a Google Sheet — an ad export, an event list, a partner's sheet. Every 15 minutes, new rows become Incoming leads. No Google sign-in needed."
        actions={
          <Can permission="crm.import.create">
            <Button onClick={() => setAdding(true)}>
              <Plus /> {t('Connect a sheet')}
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : syncs.length === 0 ? (
        <EmptyState icon={<Sheet className="h-6 w-6" />} title="No sheets connected" description="In Google Sheets choose File → Share → Publish to web → CSV, copy the link and connect it here." />
      ) : (
        <div className="space-y-2">
          {syncs.map((sync) => (
            <div key={sync.id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-4">
              <Sheet className="h-5 w-5 text-emerald-600" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{sync.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {Object.keys(sync.mapping).length} {t('columns mapped')} · {sync.lastRunAt ? `${t('checked')} ${timeAgo(sync.lastRunAt)}, ${sync.lastImported} ${t('new')}` : t('not checked yet')}
                </p>
                {sync.lastError && <p className="text-xs text-destructive">{sync.lastError}</p>}
              </div>
              <Switch checked={sync.isActive} onCheckedChange={() => toggle.mutate(sync)} />
              <Button size="sm" variant="outline" onClick={() => run.mutate(sync.id)} disabled={run.isPending}>
                <RefreshCw className={cn(run.isPending && 'animate-spin')} /> {t('Check now')}
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm(t('Disconnect this sheet? Leads already made stay.')) && remove.mutate(sync.id)}>
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
      )}
      {adding && <SheetDialog onClose={() => setAdding(false)} onSaved={refresh} />}
    </div>
  );
}

function SheetDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const { data: users = [] } = useUserOptions();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [preview, setPreview] = useState<{ headers: string[]; sample: string[][]; total: number } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importExisting, setImportExisting] = useState(false);
  const [ownerId, setOwnerId] = useState('');
  const load = useMutation({
    mutationFn: () => apiPost<{ headers: string[]; sample: string[][]; total: number }>('/sheet-syncs/preview', { csvUrl: url }),
    onSuccess: (data) => {
      setPreview(data);
      setMapping(Object.fromEntries(data.headers.map((header) => [header, guess(header)])));
    },
    onError: (error) => failed(error, 'The sheet could not be read'),
  });
  const save = useMutation({
    mutationFn: () => apiPost('/sheet-syncs', { name: name || t('Google Sheet'), csvUrl: url, mapping: Object.fromEntries(Object.entries(mapping).filter(([, field]) => field)), importExisting, ownerId: ownerId || null }),
    onSuccess: () => {
      toast.success(t('Sheet connected'));
      onSaved();
      onClose();
    },
    onError: (error) => failed(error, 'Could not connect the sheet'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('Connect a Google Sheet')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…/pub?output=csv" />
            <Button variant="outline" onClick={() => load.mutate()} disabled={!url || load.isPending}>
              {load.isPending ? <Loader2 className="animate-spin" /> : null} {t('Read')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('Publish the sheet (File → Share → Publish to web → CSV), or share it with "Anyone with the link" and paste the normal link.')}</p>
          {preview && (
            <>
              <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={t('Name, e.g. Facebook ads export')} />
              <p className="text-sm font-semibold">
                {t('Match the columns')} <span className="font-normal text-muted-foreground">({preview.total} {t('rows now')})</span>
              </p>
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {preview.headers.map((header, index) => (
                  <div key={header} className="grid grid-cols-[1fr_1fr_10rem] items-center gap-2 text-sm">
                    <span className="truncate font-semibold">{header}</span>
                    <span className="truncate text-xs text-muted-foreground">{preview.sample[0]?.[index] ?? ''}</span>
                    <Select className="h-8" value={mapping[header] ?? ''} onChange={(event) => setMapping({ ...mapping, [header]: event.target.value })}>
                      {FIELDS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {t(label)}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">{t('New leads go to')}</Label>
                  <Select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
                    <option value="">{t('Incoming, for the team')}</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <label className="flex items-center gap-2 self-end text-sm">
                  <Checkbox checked={importExisting} onCheckedChange={(checked) => setImportExisting(checked === true)} /> {t('Also import the rows already there')}
                </label>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!preview || save.isPending}>
            {t('Connect')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══ Coaching playlists ═══════════════════════════════════════════════════════

type Playlist = { id: string; name: string; description: string | null; calls: number; createdAt: string };
type PlaylistDetail = Playlist & {
  items: { id: string; note: string | null; call: { id: string; phone: string | null; direction: string; outcome: string; durationSeconds: number; notes: string | null; recordingUrl: string; startedAt: string; user: { name: string } } }[];
};

export function PlaylistsPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: lists = [], isLoading } = useQuery({ queryKey: ['playlists'], queryFn: () => apiGet<Playlist[]>('/call-playlists') });
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => apiPost('/call-playlists', { name }),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['playlists'] });
    },
    onError: (error) => failed(error, 'Could not create the playlist'),
  });
  return (
    <div>
      <PageHeader title="Coaching playlists" description="Collect the best (and the toughest) recorded calls so new people learn how your team sells. Add calls from the Calls page." />
      <Can permission="crm.call.update">
        <form
          className="mb-4 flex max-w-md gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={t('e.g. Great discovery calls')} />
          <Button type="submit" disabled={!name.trim()}>
            <Plus /> {t('Create')}
          </Button>
        </form>
      </Can>
      {isLoading ? (
        <PageLoader />
      ) : lists.length === 0 ? (
        <EmptyState icon={<ListMusic className="h-6 w-6" />} title="No playlists yet" description="Create one, then add recorded calls to it from the Calls page." />
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          {lists.map((list) => (
            <Link key={list.id} to={`/sales/coaching/${list.id}`} className="rounded-lg border bg-card p-4 hover:border-primary/50">
              <ListMusic className="h-5 w-5 text-primary" />
              <p className="mt-2 font-bold">{list.name}</p>
              <p className="text-sm text-muted-foreground">
                {list.calls} {t('calls')}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlaylistDetailPage() {
  const t = useT();
  const { playlistId = '' } = useParams();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['playlists', playlistId], queryFn: () => apiGet<PlaylistDetail>(`/call-playlists/${playlistId}`) });
  const remove = useMutation({ mutationFn: (callId: string) => apiDelete(`/call-playlists/${playlistId}/calls/${callId}`), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['playlists'] }) });
  if (isLoading || !data) return <PageLoader />;
  return (
    <div>
      <PageHeader title={data.name} breadcrumbs={[{ label: 'Coaching playlists', to: '/sales/coaching' }, { label: data.name }]} />
      {data.items.length === 0 ? (
        <EmptyState icon={<ListMusic className="h-6 w-6" />} title="No calls yet" description="Add recorded calls from the Calls page." />
      ) : (
        <div className="space-y-3">
          {data.items.map((item) => (
            <div key={item.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold">{item.call.user.name}</span>
                <span className="text-muted-foreground">
                  · {item.call.direction.toLowerCase()} · {item.call.phone ?? ''} · {formatDateTime(item.call.startedAt)} · {Math.round(item.call.durationSeconds / 60)} min
                </span>
                <Can permission="crm.call.update">
                  <button type="button" onClick={() => remove.mutate(item.call.id)} className="ms-auto text-xs text-destructive hover:underline">
                    {t('Remove')}
                  </button>
                </Can>
              </div>
              {item.note && <p className="mt-1 text-sm">{item.note}</p>}
              <audio controls preload="none" src={item.call.recordingUrl} className="mt-3 w-full" />
              {item.call.notes && <p className="mt-2 text-xs text-muted-foreground">{item.call.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** On a recorded call: play it, or add it to a coaching playlist. */
export function AddToPlaylist({ callId }: { callId: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [playlistId, setPlaylistId] = useState('');
  const [note, setNote] = useState('');
  const { data: lists = [] } = useQuery({ queryKey: ['playlists'], queryFn: () => apiGet<Playlist[]>('/call-playlists'), enabled: open });
  const add = useMutation({
    mutationFn: () => apiPost(`/call-playlists/${playlistId}/calls`, { callId, note: note || undefined }),
    onSuccess: () => {
      toast.success(t('Added to the playlist'));
      setOpen(false);
    },
    onError: (error) => failed(error, 'Could not add the call'),
  });
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title={t('Add to a coaching playlist')}>
        <ListMusic />
      </Button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('Add to a coaching playlist')}</DialogTitle>
            </DialogHeader>
            <Select value={playlistId} onChange={(event) => setPlaylistId(event.target.value)}>
              <option value="">{t('Choose a playlist…')}</option>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </Select>
            <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder={t('Why it is worth a listen (optional)')} />
            <DialogFooter>
              <Button onClick={() => add.mutate()} disabled={!playlistId || add.isPending}>
                {t('Add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ══ SEO audit ════════════════════════════════════════════════════════════════

export function SeoPanel({ kind, id }: { kind: 'post' | 'page'; id: string }) {
  const t = useT();
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['seo', kind, id],
    queryFn: () => apiGet<{ score: number; words: number; checks: { id: string; label: string; ok: boolean; advice: string }[] }>(`/growth/seo-audit/${kind}/${id}`),
  });
  if (!data) return null;
  return (
    <div className="space-y-2 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold">{t('SEO check')}</p>
        <button type="button" onClick={() => void refetch()} className="text-link text-xs font-semibold hover:underline" disabled={isFetching}>
          {t('Check again')}
        </button>
      </div>
      <p className={cn('text-3xl font-bold', data.score >= 80 ? 'text-emerald-600' : data.score >= 50 ? 'text-amber-600' : 'text-destructive')}>{data.score}</p>
      <ul className="space-y-1.5 text-xs">
        {data.checks.map((check) => (
          <li key={check.id} className="flex gap-2">
            <span className={cn('mt-0.5 h-2 w-2 shrink-0 rounded-full', check.ok ? 'bg-emerald-500' : 'bg-amber-500')} />
            <span>
              <span className="font-semibold">{t(check.label)}</span>
              {!check.ok && <span className="block text-muted-foreground">{check.advice}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
